import { type PowerSyncDrizzleDB, user } from "@journal/powersync";
import { patchRowVerified } from "../powersync/verified-write";

/**
 * ── Why ALL mutations below carry `.where(eq(user.id, userId))` ─
 *
 * Four of them (`locale`, `global_theme_id`, `is_blur`,
 * `follow_device_theme`) had this form:
 *
 *     const query = db.update(user).set({ … });
 *     await (userId === VISITOR_ID ? query : query.where(eq(user.id, userId)));
 *
 * That is, in guest mode the UPDATE went out **without `WHERE`** → it rewrote ALL
 * rows in the local `user` database. The branch existed for symmetry with
 * `getUserThemeSettingsQuery`, which for the guest reads with `.limit(1)` without
 * a filter; but a read `limit(1)` and an UPDATE without a filter are not the same
 * bet: the former picks the wrong row, the latter wipes them all.
 *
 * The premise it hung from ("the guest database has only one
 * `user` row") is false in a specific window: during the guest→real upgrade the
 * guest database CONNECTS to the real account's connector and
 * auto-subscribes to all streams — including `organizations`, which syncs
 * one `user` row for EACH co-member of that account's organizations.
 * There the table has 1 + 1 + N rows, `user` is NOT `localOnly`, and each
 * touched row enqueues its own op in `ps_crud`.
 *
 * The correct filter is NOT guessed: the guest row is seeded by
 * `ThemeProvider` (`apps/web/src/providers/theme-provider.tsx`) with
 * `db.insert(user).values({ id: uid, … })`, and for a guest tab `uid`
 * is exactly `VISITOR_ID`. So `eq(user.id, userId)` points to the SAME
 * row that the unfiltered branch intended to touch when the table had a single
 * row — identical behavior in the normal case, bounded otherwise. It is
 * also the form already used by `updateUserFontFamilyMutation` and
 * `updateUserImageMutation`, which never had a guest branch.
 *
 * Covered by `packages/shared/src/users/__tests__/mutations-where.test.ts`.
 *
 * ── Why the 6 use `patchRowVerified` (audit 2026-08-13,
 *    updated after ThemeProvider's seed guard) ────────────────────────────────
 *
 * The conversion to §4.11 (`b6f61f3`) excluded `user` from `patchRowVerified`
 * by copying the canonical argument: "the server creates the row / the guest's
 * seed is not synced, so there is no checkpoint window that
 * rebuilds it." An independent audit (2026-08-13,
 * `infra/reports/user-exclusion-audit/2026-08-13-summary.md`) measured that this
 * argument was never verified and is FALSE here, as in the other 10 repos that
 * copied the same comment.
 *
 * At that time, `ThemeProvider`'s `createEffect`
 * (`apps/web/src/providers/theme-provider.tsx`) made the window worse: it inserted
 * the LOCAL row for ANY `uid` — it only checked `if (!db || !uid)
 * return`, and did not distinguish `VISITOR_ID` from a real account — so its phantom
 * insert competed with the row the server had already created. A later
 * commit, the same day, closed THAT concrete cause: the effect now only
 * seeds when `uid === VISITOR_ID` (see the guard and its reasoning in
 * `theme-provider.tsx`).
 *
 * **That does NOT make this protection unnecessary.** `patchRowVerified` is still
 * needed for `user` because of a window INDEPENDENT of the phantom insert,
 * which the guard does not touch:
 *
 *   - `db` (the PowerSync accessor) becomes non-null as soon as the local
 *     SQLite opens — it does not wait for the initial sync to finish
 *     (`apps/web/src/providers/power-sync-context.tsx`: `connect()` is async
 *     and happens after opening the DB).
 *   - `userId()` becomes active as soon as `signUpActor`/`signInActor` receive
 *     the server session (`packages/shared/src/auth/controllers.ts`) — an
 *     independent REST call from the PowerSync stream.
 *
 * Between the time `db`+`userId()` are ready and the time the initial checkpoint
 * brings the `user` row the server already created, there is still a window in which
 * a normal `UPDATE` (change language, theme, blur…) can hit a row
 * the local view does not yet have: it matches ZERO rows and silently returns "success".
 * That window exists even if `ThemeProvider` had never seeded
 * anything — the view synchronizing, nothing more. If `patchRowVerified` is ever
 * removed from `user`, it should be because it is proven that THIS window
 * (not the phantom-insert window, already closed) no longer exists.
 *
 * No `options.scope` is needed: `user` has no organization or
 * branch dimension — `eq(user.id, userId)` is already the only meaningful predicate (one
 * row per user). Nor `hasUpdatedAt: false` (the table DOES have
 * `updated_at`, see `packages/powersync/src/schema.ts`) nor `verifyBy:
 * "updated_at"` (no patch here is a SQL expression that cannot be compared by
 * value).
 *
 * Red→green: `packages/shared/src/powersync/__tests__/verified-write.test.ts`
 * reproduces the window with the old form (CONTROL) and exercises the 6
 * mutations inside it.
 */

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale });
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId });
}

/**
 * Writes the avatar KEY to the local row.
 *
 * It is written local-first (just like language, font, and theme) and not via REST:
 * this way the new photo appears instantly and survives a cold reload without a
 * connection. The BINARY does go via REST (`POST /api/avatar`) because it does not
 * fit in the sync queue; only the key returned by that upload travels here.
 *
 * `null` = "remove the photo" (the initials return).
 *
 * The guest has no synchronizable row of its own and cannot have an avatar (rule
 * zero), so there is no `VISITOR_ID` branch here as in `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { image: imageKey });
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId });
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 });
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, {
    follow_device_theme: followDevice ? 1 : 0,
  });
}
